// Settings → Quick Links.
//
// Lets admins configure up to 5 sidebar shortcuts that appear for all staff
// at their company.  The editor is a list of slot rows: route picker + label
// input + remove / reorder buttons.

import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { fetchQuickLinks, saveQuickLinks } from '../../../api/quickLinks';
import { useToast } from '../../../hooks/useToast';
import styles from './QuickLinksSettingsPage.module.css';

// ---------------------------------------------------------------------------
// Available routes constant
// ---------------------------------------------------------------------------

export const AVAILABLE_ROUTES = [
  { group: 'Collections', label: 'Collections Dashboard', route: '/collections/dashboard' },
  { group: 'Collections', label: 'Bookings', route: '/collections/bookings' },
  { group: 'Collections', label: 'Receipts', route: '/collections/receipts' },
  { group: 'Collections', label: 'Receipts — Awaiting Approval', route: '/collections/receipts/approval' },
  { group: 'Collections', label: 'Invoices', route: '/collections/invoices' },
  { group: 'Collections', label: 'Payment Runs', route: '/collections/payment-run' },
  { group: 'Collections', label: 'BACS', route: '/collections/bacs' },
  { group: 'Collections', label: 'Diary', route: '/collections/diary' },
  { group: 'Collections', label: 'Pending', route: '/collections/pending' },
  { group: 'Collections', label: 'GeoLink', route: '/collections/geolink' },
  { group: 'Collections', label: 'Driver Map', route: '/collections/geolink/driver-map' },
  { group: 'Collections', label: 'Integrations', route: '/collections/integrations' },
  { group: 'Skip Manager', label: 'Skip Dashboard', route: '/skip/dashboard' },
  { group: 'Skip Manager', label: 'Skip Jobs', route: '/skip/jobs' },
  { group: 'Skip Manager', label: 'Skip Enquiries', route: '/skip/enquiries' },
  { group: 'Skip Manager', label: 'Skip Inventory', route: '/skip/inventory' },
  { group: 'Weighbridge', label: 'Weighbridge Dashboard', route: '/weighbridge/dashboard' },
  { group: 'Weighbridge', label: 'WB Tickets', route: '/weighbridge/tickets' },
  { group: 'Weighbridge', label: 'WB Customers', route: '/weighbridge/customers' },
  { group: 'Weighbridge', label: 'WB Vehicles', route: '/weighbridge/vehicles' },
  { group: 'Reports', label: 'Reports Hub', route: '/reports' },
  { group: 'Reports', label: 'Collections Reports', route: '/reports/collections' },
  { group: 'Customers & Admin', label: 'Customers', route: '/customers' },
  { group: 'Customers & Admin', label: 'Users', route: '/admin/users' },
  { group: 'Customers & Admin', label: 'Drivers', route: '/admin/drivers' },
  { group: 'Settings', label: 'Settings', route: '/settings' },
  { group: 'Settings', label: 'Company Settings', route: '/settings/company' },
  { group: 'Settings', label: 'My Profile', route: '/profile' },
] as const;

type RouteEntry = { group: string; label: string; route: string };

// Group the routes so we can render <optgroup> elements.
function groupRoutes(): { group: string; options: RouteEntry[] }[] {
  const groups: { group: string; options: RouteEntry[] }[] = [];
  for (const r of AVAILABLE_ROUTES) {
    let g = groups.find((x) => x.group === r.group);
    if (!g) {
      g = { group: r.group, options: [] };
      groups.push(g);
    }
    g.options.push(r as RouteEntry);
  }
  return groups;
}

const ROUTE_GROUPS = groupRoutes();
const DEFAULT_ROUTE = AVAILABLE_ROUTES[0].route;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LinkSlot = { _id: number; label: string; route: string };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QuickLinksSettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const nextId = useRef(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState<LinkSlot[]>([]);
  const [savedLinks, setSavedLinks] = useState<LinkSlot[]>([]);

  // Fetch on mount
  useEffect(() => {
    let cancelled = false;
    fetchQuickLinks()
      .then((dtos) => {
        if (cancelled) return;
        const slots: LinkSlot[] = dtos.map((d) => ({ _id: nextId.current++, label: d.label, route: d.route }));
        setLinks(slots);
        setSavedLinks(slots);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load quick links');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDirty = JSON.stringify(links) !== JSON.stringify(savedLinks);

  // ---------------------------------------------------------------------------
  // Slot mutation helpers
  // ---------------------------------------------------------------------------

  function addLink() {
    if (links.length >= 5) return;
    setLinks((prev) => [...prev, { _id: nextId.current++, route: DEFAULT_ROUTE, label: '' }]);
  }

  function removeLink(idx: number) {
    setLinks((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateRoute(idx: number, route: string) {
    setLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, route } : l)));
  }

  function updateLabel(idx: number, label: string) {
    setLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, label } : l)));
  }

  function moveUp(idx: number) {
    if (idx === 0) return;
    setLinks((prev) => {
      const next = [...prev];
      const above = next[idx - 1];
      const current = next[idx];
      if (!above || !current) return prev;
      next[idx - 1] = current;
      next[idx] = above;
      return next;
    });
  }

  function moveDown(idx: number) {
    if (idx === links.length - 1) return;
    setLinks((prev) => {
      const next = [...prev];
      const current = next[idx];
      const below = next[idx + 1];
      if (!current || !below) return prev;
      next[idx] = below;
      next[idx + 1] = current;
      return next;
    });
  }

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  async function handleSave() {
    if (links.some((l) => l.label.trim() === '')) {
      toast.error('Each link must have a display name');
      return;
    }
    setSaving(true);
    try {
      await saveQuickLinks(links.map(({ label, route }) => ({ label, route })));
      setSavedLinks([...links]);
      toast.success('Quick links saved');
    } catch {
      toast.error('Failed to save quick links');
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Quick Links" />
        <Card>
          <div className={styles.spinnerWrap}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Quick Links"
        subtitle="Add up to 5 shortcuts that appear in the sidebar for all staff at your company."
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/settings')}>
            Back
          </Button>
        }
      />

      <Card>
        <div className={styles.editor}>
          {links.length === 0 && (
            <p className={styles.emptyHint}>
              No quick links yet. Click &ldquo;+ Add Link&rdquo; to add your first shortcut.
            </p>
          )}

          {links.map((link, idx) => (
            <div key={link._id} className={styles.linkRow}>
              {/* Page picker */}
              <div className={styles.fieldGroup}>
                <label htmlFor={`ql-route-${idx}`} className={styles.fieldLabel}>
                  Page
                </label>
                <select
                  id={`ql-route-${idx}`}
                  className={styles.routeSelect}
                  value={link.route}
                  onChange={(e) => updateRoute(idx, e.target.value)}
                >
                  {ROUTE_GROUPS.map((g) => (
                    <optgroup key={g.group} label={g.group}>
                      {g.options.map((o) => (
                        <option key={o.route} value={o.route}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Label input */}
              <div className={styles.fieldGroup}>
                <label htmlFor={`ql-label-${idx}`} className={styles.fieldLabel}>
                  Display name
                </label>
                <input
                  id={`ql-label-${idx}`}
                  type="text"
                  className={styles.labelInput}
                  maxLength={100}
                  placeholder="e.g. Today's Bookings"
                  value={link.label}
                  onChange={(e) => updateLabel(idx, e.target.value)}
                />
              </div>

              {/* Reorder + remove */}
              <div className={styles.rowButtons}>
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label="Move up"
                  disabled={idx === 0}
                  onClick={() => moveUp(idx)}
                >
                  <ChevronUp size={16} aria-hidden />
                </button>
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label="Move down"
                  disabled={idx === links.length - 1}
                  onClick={() => moveDown(idx)}
                >
                  <ChevronDown size={16} aria-hidden />
                </button>
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label="Remove link"
                  onClick={() => removeLink(idx)}
                >
                  <X size={16} aria-hidden />
                </button>
              </div>
            </div>
          ))}

          <div className={styles.addRow}>
            <Button
              variant="secondary"
              disabled={links.length >= 5}
              onClick={addLink}
            >
              + Add Link
            </Button>
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            variant="primary"
            icon={Save}
            loading={saving}
            onClick={() => { void handleSave(); }}
          >
            Save Changes
          </Button>
          {isDirty && (
            <span className={styles.unsavedIndicator} aria-live="polite">
              Unsaved changes
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
